export const formatDobInput = (value: string) => {
  let digits = value.replace(/\D/g, '').slice(0, 8);
  if (!digits) return '';
  let day = digits.slice(0, 2);
  let month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (day.length === 1 && Number(day) > 3) {
    day = `0${day}`;
  }
  if (day.length === 2) {
    const dayNum = Math.min(Math.max(Number(day), 1), 31);
    day = String(dayNum).padStart(2, '0');
  }
  if (month.length === 1 && Number(month) > 1) {
    month = `0${month}`;
  }
  if (month.length === 2) {
    const monthNum = Math.min(Math.max(Number(month), 1), 12);
    month = String(monthNum).padStart(2, '0');
  }

  if (digits.length <= 2) return day;
  if (digits.length <= 4) return `${day}-${month}`;
  return `${day}-${month}-${year}`;
};

export const formatDobFromIso = (value: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const datePart = trimmed.includes('T') ? trimmed.split('T')[0] : trimmed.split(' ')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split('-');
    return `${day}-${month}-${year}`;
  }
  return trimmed.replace(/\//g, '-');
};

export const normalizeDobToIso = (value: string) => {
  if (!value) return '';
  const match = value.match(/^(\d{2})([-/])(\d{2})\2(\d{4})$/);
  if (match) {
    const day = match[1];
    const month = match[3];
    const year = match[4];
    return `${year}-${month}-${day}`;
  }
  return '';
};

// Attempts to parse any common autofill DOB format into the DD-MM-YYYY display
// format used by the text input. Returns null if the value is unrecognisable.
export const parseDobToDisplay = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null;

  // Already DD-MM-YYYY or DD/MM/YYYY
  const ddmmyyyy = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (ddmmyyyy) {
    const day = ddmmyyyy[1].padStart(2, '0');
    const month = ddmmyyyy[2].padStart(2, '0');
    const year = ddmmyyyy[3];
    return `${day}-${month}-${year}`;
  }

  // ISO YYYY-MM-DD (browser autofill, Edge)
  const iso = v.match(/^(\d{4})[\/\-](\d{2})[\/\-](\d{2})/);
  if (iso) {
    return `${iso[3]}-${iso[2]}-${iso[1]}`;
  }

  // 8 digits with no separators: DDMMYYYY
  const compact = v.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (compact) {
    return `${compact[1]}-${compact[2]}-${compact[3]}`;
  }

  return null;
};

export const formatCardNumber = (value: string, maxLength = 19) => {
  const rawDigits = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const digits = rawDigits.slice(0, Math.max(0, maxLength));
  const parts = digits.match(/.{1,4}/g);
  return parts ? parts.join(' ') : digits;
};

export const formatExpiry = (value: string) => {
  let digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length === 1 && Number(digits) > 1) {
    digits = `0${digits}`;
  }
  if (digits.length >= 2) {
    let month = digits.slice(0, 2);
    const monthNum = Math.min(Math.max(Number(month), 1), 12);
    month = String(monthNum).padStart(2, '0');
    const year = digits.slice(2);
    return year ? `${month}/${year}` : month;
  }
  return digits;
};

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  pound: '£',
};

// Plain-string HTML-to-text so it also runs under vitest's node environment
// (no DOMParser) and never interprets the markup as live DOM.
export const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&([a-z]+);/gi, (match, name) => NAMED_ENTITIES[name.toLowerCase()] ?? match)
    .replace(/\s+/g, ' ')
    .trim();
};

export const toMoneyNumber = (value: unknown): number => {
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseFloat(value)
        : Number.NaN;
  return Number.isFinite(numericValue) ? numericValue : 0;
};
